/**
 * WebSocket Service for Real-time Alert Notifications
 * Handles WebSocket connection, reconnection, and message processing
 */

class WebSocketService {
  constructor() {
    this.ws = null;
    this.userId = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000; // 3 seconds
    this.heartbeatInterval = 30000; // 30 seconds
    this.heartbeatTimer = null;
    this.isConnecting = false;
    this.messageHandlers = new Map();
    this.connectionState = 'disconnected'; // disconnected, connecting, connected, error
    
    // Bind methods to preserve context
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.send = this.send.bind(this);
    this.onMessage = this.onMessage.bind(this);
    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onError = this.onError.bind(this);
    this.startHeartbeat = this.startHeartbeat.bind(this);
    this.stopHeartbeat = this.stopHeartbeat.bind(this);
  }

  /**
   * Connect to WebSocket server
   * @param {string} userId - User ID for the connection
   * @param {string} baseUrl - Base URL for the WebSocket connection
   */
  connect(userId, baseUrl = 'ws://localhost:8000') {
    if (this.isConnecting || this.connectionState === 'connected') {
      console.log('🔌 [WEBSOCKET] Already connecting or connected');
      return;
    }

    this.userId = userId;
    this.isConnecting = true;
    this.connectionState = 'connecting';

    const wsUrl = `${baseUrl}/api/ws/${userId}`;
    console.log(`🔌 [WEBSOCKET] Connecting to: ${wsUrl}`);

    try {
      this.ws = new WebSocket(wsUrl);
      this.ws.onopen = this.onOpen;
      this.ws.onmessage = this.onMessage;
      this.ws.onclose = this.onClose;
      this.ws.onerror = this.onError;
    } catch (error) {
      console.error('❌ [WEBSOCKET] Connection error:', error);
      this.connectionState = 'error';
      this.isConnecting = false;
      this.handleReconnect();
    }
  }

  /**
   * Handle successful WebSocket connection
   */
  onOpen(event) {
    console.log('✅ [WEBSOCKET] Connected successfully');
    this.connectionState = 'connected';
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.startHeartbeat();
    
    // Notify handlers of connection
    this.notifyHandlers('connection_established', {
      message: 'Connected to real-time alert system',
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Handle incoming WebSocket messages
   */
  onMessage(event) {
    try {
      const message = JSON.parse(event.data);
      console.log('📨 [WEBSOCKET] Received message:', message);

      // Handle different message types
      switch (message.type) {
        case 'connection_established':
          console.log('🔌 [WEBSOCKET] Connection established');
          break;
          
        case 'new_alert':
          console.log('🚨 [WEBSOCKET] New alert received:', message.data);
          this.notifyHandlers('new_alert', message.data);
          break;
          
        case 'alert_update':
          console.log('📝 [WEBSOCKET] Alert update received:', message.data);
          this.notifyHandlers('alert_update', message.data);
          break;
          
        case 'system_message':
          console.log('📢 [WEBSOCKET] System message:', message.message);
          this.notifyHandlers('system_message', message);
          break;
          
        case 'pong':
          console.log('🏓 [WEBSOCKET] Pong received');
          break;
          
        case 'connection_status':
          console.log('📊 [WEBSOCKET] Connection status:', message);
          this.notifyHandlers('connection_status', message);
          break;
          
        case 'camera_status_update':
          console.log('📡 [WEBSOCKET] Camera status update:', message);
          this.notifyHandlers('camera_status_update', message);
          break;
          
        default:
          console.log('❓ [WEBSOCKET] Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('❌ [WEBSOCKET] Error parsing message:', error);
    }
  }

  /**
   * Handle WebSocket connection close
   */
  onClose(event) {
    console.log('🔌 [WEBSOCKET] Connection closed:', event.code, event.reason);
    this.connectionState = 'disconnected';
    this.isConnecting = false;
    this.stopHeartbeat();
    
    // Notify handlers of disconnection
    this.notifyHandlers('connection_closed', {
      code: event.code,
      reason: event.reason,
      timestamp: new Date().toISOString()
    });

    // Attempt to reconnect if not a normal closure
    if (event.code !== 1000) {
      this.handleReconnect();
    }
  }

  /**
   * Handle WebSocket errors
   */
  onError(error) {
    console.error('❌ [WEBSOCKET] Error:', error);
    this.connectionState = 'error';
    this.isConnecting = false;
    
    // Notify handlers of error
    this.notifyHandlers('connection_error', {
      error: error.message || 'WebSocket error',
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Handle reconnection logic
   */
  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('❌ [WEBSOCKET] Max reconnection attempts reached');
      this.connectionState = 'error';
      this.notifyHandlers('reconnect_failed', {
        attempts: this.reconnectAttempts,
        timestamp: new Date().toISOString()
      });
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectInterval * this.reconnectAttempts;
    
    console.log(`🔄 [WEBSOCKET] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      if (this.userId) {
        this.connect(this.userId);
      }
    }, delay);
  }

  /**
   * Start heartbeat to keep connection alive
   */
  startHeartbeat() {
    this.stopHeartbeat(); // Clear any existing heartbeat
    
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping' });
      }
    }, this.heartbeatInterval);
  }

  /**
   * Stop heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Send message through WebSocket
   */
  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(message));
        console.log('📤 [WEBSOCKET] Sent message:', message);
      } catch (error) {
        console.error('❌ [WEBSOCKET] Error sending message:', error);
      }
    } else {
      console.warn('⚠️ [WEBSOCKET] Cannot send message - not connected');
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    console.log('🔌 [WEBSOCKET] Disconnecting...');
    this.stopHeartbeat();
    
    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
      this.ws = null;
    }
    
    this.connectionState = 'disconnected';
    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  /**
   * Register a message handler
   */
  on(eventType, handler) {
    if (!this.messageHandlers.has(eventType)) {
      this.messageHandlers.set(eventType, []);
    }
    this.messageHandlers.get(eventType).push(handler);
  }

  /**
   * Unregister a message handler
   */
  off(eventType, handler) {
    if (this.messageHandlers.has(eventType)) {
      const handlers = this.messageHandlers.get(eventType);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Notify all handlers for an event type
   */
  notifyHandlers(eventType, data) {
    if (this.messageHandlers.has(eventType)) {
      this.messageHandlers.get(eventType).forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`❌ [WEBSOCKET] Error in handler for ${eventType}:`, error);
        }
      });
    }
  }

  /**
   * Get connection state
   */
  getConnectionState() {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.connectionState === 'connected' && this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Request connection status
   */
  requestConnectionStatus() {
    this.send({ type: 'get_connection_status' });
  }
}

// Create and export a singleton instance
const websocketService = new WebSocketService();
export default websocketService;
